/**
 * If the input is not hidden, has a width and height, and has an offset parent, then it is focusable
 * @param input - The input element to check.
 * @returns A boolean value.
 */
export const isFocusableInput = (input) => {
  if (!input.offsetParent || input.offsetParent === "body") return false;
  if (input.type === "hidden") return false;
  if (!input.clientWidth || !input.clientHeight) return false;
  return true;
};

/**
 * If the input is a select, button, or a date, color, or file input, then it has its own enter
 * handler.
 * @param input - The input element that the user is currently focused on.
 * @returns A boolean value.
 */
export const inputHasOwnEnterHandler = (input) => {
  return (
    input.tagName === "SELECT" ||
    input.tagName === "BUTTON" ||
    input.tagName === "TEXTAREA" ||
    ["date", "color", "file"].includes(input.type)
  );
};

/**
 * It takes an input and a list of elements, and focuses on the next focusable element in the list
 * @param input - the input element that is currently focused
 * @param elements - an array of elements that you want to focus on
 */
export const focusOnNextField = (input, elements) => {
  const focusableElements = Array.from(elements).filter(isFocusableInput);
  const currentIndex = focusableElements.indexOf(input);
  const nextIndex = currentIndex + 1;
  if (focusableElements[nextIndex]) {
    focusableElements[nextIndex].focus();
  }
};

/**
 * It adds an event listener to all elements that match the selector, and when the enter key is
 * pressed, it focuses on the next field
 * @param {String} selector - a string that represents the selector of the form you want to add the enter
 * hitting functionality to.
 * @param {Function} onEnterPressCallback - a function that will be called when the enter key is pressed.
 */
export const initEnterPressInterception = (selector, onEnterPressCallback) => {
  document.querySelectorAll(selector).forEach((form) => {
    form.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        const elements = form.querySelectorAll("input, select, button");
        if (inputHasOwnEnterHandler(event.target)) {
          return;
        }
        focusOnNextField(event.target, elements);
        if (onEnterPressCallback && onEnterPressCallback instanceof Function) {
          onEnterPressCallback(event);
        }
        event.preventDefault();
      }
    });
  });
};
